import { useState } from "react";
import { X, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { opsApi } from "../lib/api";

const BUCKETS = ["to", "cc", "bcc"];

/** Email tags: pin people to the automated emails this project sends. Nothing pinned = the
 *  global defaults from Settings › Email. */
export default function EmailRoutingDialog({ project, people, onClose, onSaved }) {
  const routing = project.email_routing || { routes: {}, templates: {}, inherited: {} };
  const [routes, setRoutes] = useState(routing.routes || {});
  const [busy, setBusy] = useState(false);
  const inherited = routing.inherited || {};

  const rowOf = (key) => ({ to: [], cc: [], bcc: [], ...(routes[key] || {}) });
  const isSet = (key) => BUCKETS.some((b) => (rowOf(key)[b] || []).length > 0);

  const write = (key, row) => setRoutes((prev) => ({ ...prev, [key]: row }));

  const toggle = (key, bucket, email) => {
    const row = rowOf(key);
    const has = (row[bucket] || []).includes(email);
    write(key, {
      ...Object.fromEntries(BUCKETS.map((b) => [b, (row[b] || []).filter((e) => e !== email)])),
      [bucket]: has ? row[bucket].filter((e) => e !== email) : [...(row[bucket] || []), email],
    });
  };

  const allInRow = (key, bucket) => {
    const row = rowOf(key);
    const everyone = people.map((p) => p.email);
    const already = everyone.every((e) => (row[bucket] || []).includes(e));
    write(key, already
      ? { ...row, [bucket]: [] }
      : { ...Object.fromEntries(BUCKETS.map((b) => [b, b === bucket ? everyone : []])) });
  };

  const allForPerson = (email, bucket) => {
    const keys = (routing.templates || []).map((t) => t.key);
    const already = keys.every((k) => (rowOf(k)[bucket] || []).includes(email));
    setRoutes((prev) => {
      const next = { ...prev };
      keys.forEach((k) => {
        const row = { to: [], cc: [], bcc: [], ...(next[k] || {}) };
        const cleared = Object.fromEntries(
          BUCKETS.map((b) => [b, (row[b] || []).filter((e) => e !== email)]));
        next[k] = already ? cleared : { ...cleared, [bucket]: [...cleared[bucket], email] };
      });
      return next;
    });
  };

  const save = async () => {
    setBusy(true);
    try {
      await opsApi.saveEmailRouting(project.id, routes);
      toast.success("Email tags saved");
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save those tags");
    } finally { setBusy(false); }
  };

  const Pill = ({ on, ghost, label, onClick, testId }) => (
    <button onClick={onClick} data-testid={testId}
            className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase transition-colors ${
              on ? "bg-blue-500 text-white shadow-sm"
                : ghost ? "border border-dashed border-slate-300 bg-slate-50 text-slate-400"
                  : "border border-slate-200 text-slate-400 hover:border-slate-400"}`}>
      {label}
    </button>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/40 p-4"
         onClick={onClose}>
      <div className="my-6 w-full max-w-5xl rounded-xl bg-white p-5 shadow-xl"
           onClick={(e) => e.stopPropagation()} data-testid="ops-email-routing-dialog">
        <div className="flex items-start justify-between gap-3">
          <h3 className="text-base font-semibold text-slate-900">Set emails</h3>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="ops-routing-close">
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="mt-4 max-h-[62vh] overflow-auto rounded-lg border border-slate-200">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-slate-50 text-[10px] uppercase text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left">Email</th>
                {people.map((p) => (
                  <th key={p.email} className="px-2 py-2 text-center">
                    <span className="block max-w-[7rem] truncate normal-case">{p.name}</span>
                    <span className="block text-[9px] font-normal text-slate-400">{p.group}</span>
                    <span className="mt-1 inline-flex gap-1">
                      {BUCKETS.map((b) => (
                        <Pill key={b} label={b} testId={`ops-routing-all-${b}-${p.email}`}
                              on={(routing.templates || []).length > 0
                                && (routing.templates || []).every(
                                  (t) => (rowOf(t.key)[b] || []).includes(p.email))}
                              onClick={() => allForPerson(p.email, b)} />
                      ))}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(routing.templates || []).map((t) => {
                const inh = inherited[t.key] || {};
                const overridden = isSet(t.key);
                return (
                  <tr key={t.key} data-testid={`ops-routing-row-${t.key}`}>
                    <td className="px-3 py-2">
                      <span className="font-medium text-slate-800">{t.name}</span>
                      {!overridden && (
                        <span className="ml-2 text-[10px] uppercase text-slate-400"
                              data-testid={`ops-routing-inherited-${t.key}`}>
                          {BUCKETS.some((b) => (inh[b] || []).length) ? "inherited" : t.audience_label}
                        </span>
                      )}
                      <span className="mt-1 flex gap-1">
                        {BUCKETS.map((b) => (
                          <Pill key={b} label={`all ${b}`} testId={`ops-routing-row-all-${t.key}-${b}`}
                                on={people.length > 0 && people.every(
                                  (p) => (rowOf(t.key)[b] || []).includes(p.email))}
                                onClick={() => allInRow(t.key, b)} />
                        ))}
                      </span>
                    </td>
                    {people.map((p) => {
                      const row = rowOf(t.key);
                      return (
                        <td key={p.email} className="px-2 py-2 text-center">
                          <span className="inline-flex gap-1">
                            {BUCKETS.map((b) => (
                              <Pill key={b} label={b}
                                    testId={`ops-routing-${t.key}-${b}-${p.email}`}
                                    on={(row[b] || []).includes(p.email)}
                                    ghost={!overridden && (inh[b] || []).includes(p.email)}
                                    onClick={() => toggle(t.key, b, p.email)} />
                            ))}
                          </span>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {people.length === 0 && (
          <p className="mt-3 text-xs text-amber-700" data-testid="ops-routing-no-people">
            Nobody is on this job yet — add the ABS team or a GC contact first.
          </p>
        )}

        <div className="mt-4 flex items-center justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={save} disabled={busy} data-testid="ops-routing-save">
            <Check className="mr-1.5 h-3.5 w-3.5" /> Save tags
          </Button>
        </div>
      </div>
    </div>
  );
}
